import MarkdownRender from 'LuxKit/MarkdownRender'
import { FormattedMessage, useIntl } from 'react-intl'
import OfferDescription from '../OfferDescription'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import { mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-inline-start: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

interface Props {
  offer: App.HotelOffer
  property: App.OfferProperty
  location: string
}

function LastMinutePropertyDetails(props: Props) {
  const { offer, property, location } = props
  const intl = useIntl()
  const propertyLogoAlt = property.name
    ? intl.formatMessage({
        id: '9lClZXH1B9wnNAws3XbG',
        defaultMessage: '{propertyName} logo',
        description: 'Alt text for a property logo shown in the last minute offer details header when the property name is known',
      }, { propertyName: property.name })
    : intl.formatMessage({
        id: '1I4eE926UEJDNCJAzz-E',
        defaultMessage: 'Vendor logo',
        description: 'Fallback alt text for a property logo shown in the last minute offer details header',
      })
  return (<>
    <Header>
      <VerticalSpacer gap={8}>
        <BodyTextBlock variant="medium" data-testid="offer-location" startIcon={<LineMapMarkerIcon />}>
          <FormattedMessage
            id="IxL6Aa9kvyzfJ2g4oNdp"
            defaultMessage="{location} · <link>view map</link>"
            description="Property location with link to view map on the map or location view"
            values={{
              location,
              link: (chunks) => <TextLink href="#getting-there">{chunks}</TextLink>,
            }}
          />
        </BodyTextBlock>
        <Heading variant="heading2" as="h1">{offer.property.name}</Heading>
        <BodyText variant="large" as="h2">{offer.name}</BodyText>
      </VerticalSpacer>
      {property.logoImageId &&
        <PropertyLogo
          dpr={2}
          height={75}
          enhancedFormats={false}
          id={property.logoImageId}
          alt={propertyLogoAlt}
        />}
    </Header>
    <div>
      {offer.description && <OfferDescription description={offer.description} />}
      {offer.additionalDescription && <MarkdownRender type="compact" fontSize="medium" content={offer.additionalDescription} />}
    </div>
  </>)
}

export default LastMinutePropertyDetails
